import { digger } from '@cbre/digger';
import { areaUomAbbrev, concatValuesBy, defaultTxt, EMPTY_STRING, formatNumber, formatUomValues } from '@miq/utils';
import { map } from 'rxjs/operators';
import { ReportRecord } from '../../../../../models/report.model';
import { LovValues, TemplateFieldsDisplayFn } from '../../../../../models/template.model';
import { displayFnWrapper, mapAndFormatDynamicValue } from '../../../utils/dynamic-data-retrievers';
import { DOLLAR_SIGN, getUOMValue } from '../us.common';

export const displayFnMap: TemplateFieldsDisplayFn = {
  268: {
    // 268 Building Name
    displayFn: displayFnWrapper,
  },
  270: {
    // 270 Address
    displayFn: (record: ReportRecord, _, street, subMarket, district) => {
      const values = [subMarket, district].map(arg => {
        const mappedValue = mapAndFormatDynamicValue(record, arg) as string;
        const value = mappedValue == null ? EMPTY_STRING : mappedValue;
        return value === defaultTxt ? EMPTY_STRING : value;
      });
      const topVal = mapAndFormatDynamicValue(record, street);
      const bottomVal = values.every(value => value == EMPTY_STRING)
        ? defaultTxt
        : values.some(value => value == EMPTY_STRING)
          ? values.join('')
          : values.join('/');
      return concatValuesBy(' \n', topVal, bottomVal);
    },
  },
  271: {
    // 271 Tenant
    displayFn: (record: ReportRecord, _, tenantName) => {
      const tenant = mapAndFormatDynamicValue(record, tenantName, null, null, null, ' ');
      if (Array.isArray(tenant)) {
        return tenant.map((t, i) => {
          if (i !== tenant.length - 1) {
            return t + ', ';
          }
          return t;
        });
      }
      return tenant;
    },
  },
  272: {
    // 272 Size
    displayFn: displayFnWrapper,
  },
  273: {
    // 273 Base Rent / Rent Type
    displayFn: (record: ReportRecord, _, baseRent, rateType) => {
      const [rent] = mapAndFormatDynamicValue(record, baseRent, [], false);
      const values = concatValuesBy(
        ' \n',
        rent == null || rent === defaultTxt ? defaultTxt : DOLLAR_SIGN + rent,
        mapAndFormatDynamicValue(record, rateType)
      );
      return values;
    },
  },
  274: {
    // 274 EFF Rent / Escalation
    displayFn: (record: ReportRecord, _, effRent, escalation) => {
      const [rent] = mapAndFormatDynamicValue(record, effRent, [], false);
      const values = concatValuesBy(
        ' \n',
        rent == null || rent === defaultTxt ? defaultTxt : DOLLAR_SIGN + rent,
        mapAndFormatDynamicValue(record, escalation)
      );
      return values;
    },
  },
  275: {
    // 275 Sign Date Term
    displayFn: (record: ReportRecord, _, signDate, leaseTerm, uom) => {
      const [mappedTerm] = mapAndFormatDynamicValue(record, leaseTerm);
      const mappedTermUOM = mapAndFormatDynamicValue(record, uom) as String;
      const term = mappedTerm == null ? defaultTxt : mappedTerm;
      const termUOM = mappedTermUOM == null ? defaultTxt : mappedTermUOM;
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, signDate),
        term === defaultTxt && termUOM === defaultTxt ? defaultTxt : term + ' ' + termUOM
      );
      return values;
    },
  },
  276: {
    // 276 Start / End Date
    displayFn: (record: ReportRecord, _, startDate, endDate) => {
      const values = concatValuesBy(
        ' \n',
        mapAndFormatDynamicValue(record, startDate),
        mapAndFormatDynamicValue(record, endDate)
      );
      return values;
    },
  },
  277: {
    // 277 Free Rent / TIA
    displayFn: (record: ReportRecord, _, freeRent, tiAllowance) => {
      const fRent = formatUomValues(mapAndFormatDynamicValue(record, freeRent), 'Months');
      const [tiaValue] = mapAndFormatDynamicValue(record, tiAllowance);
      const tia = tiaValue == null || tiaValue === defaultTxt ? defaultTxt : DOLLAR_SIGN + tiaValue;
      const values = concatValuesBy(' \n', fRent, tia);
      return values;
    },
  },
  278: {
    // 278 Floor / Suite
    displayFn: (record: ReportRecord, _, floor, suite) => {
      const values = concatValuesBy(
        ' | ',
        mapAndFormatDynamicValue(record, floor),
        mapAndFormatDynamicValue(record, suite)
      );
      return values;
    },
  },
  332: {
    // 329 - Total Available SF
    displayFn: (record: ReportRecord[], _, space) => {
      let totalSize = 0;
      let uom = null;
      record.forEach(rec => {
        const size = digger(rec.data, space.field);
        const uomValue = space.uomArgument ? digger(rec.data, space.uomArgument.field) : null;
        uom = uomValue ? getUOMValue(uom, uomValue) : null;
        if (size) {
          totalSize += parseInt(size);
        }
      });
      if (space.uomArgument) {
        return space.uomArgument.lovValues.pipe(
          map((currencyLOV: LovValues[]) => {
            if (uom && currencyLOV) {
              const lovValue = currencyLOV.find(a => a.key === uom);
              if (lovValue) {
                uom = areaUomAbbrev(lovValue.value);
              }
            }
            return formatNumber(totalSize) + ' ' + (uom || '');
          })
        );
      } else {
        return formatNumber(totalSize) + ' SF';
      }
    },
  },
};
